import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toPascal, toCamel } from './utils';

function javaType(t: string): string {
  return t === 'number' ? 'double' : t === 'boolean' ? 'boolean' : t === 'array' ? 'List<Object>' : t === 'object' ? 'Map<String, Object>' : 'String';
}

function genConfig(config: ExportConfig): string {
  const appEntries = config.apps
    .map((a) => `        APPS.put("${a.key}", new AppConfig("${a.fromEmail}", "${a.envVarName}"));`)
    .join("\n");

  const templateEntries = config.apps
    .flatMap((a) =>
      a.templates.map((t) => `        TEMPLATES.put("${t.camelName}", new TemplateConfig("${a.key}", "${t.slug}"));`)
    )
    .join("\n");

  return `// MailConfig.java — generated by Mail Service SDK Export
import java.util.*;

public class MailConfig {
    public record AppConfig(String fromEmail, String keyEnv) {}
    public record TemplateConfig(String app, String slug) {}

    public static final Map<String, AppConfig> APPS;
    public static final Map<String, TemplateConfig> TEMPLATES;

    static {
        APPS = new HashMap<>();
${appEntries}

        TEMPLATES = new HashMap<>();
${templateEntries}
    }
}
`;
}

function genService(config: ExportConfig): string {
  const typedMethods = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const methodName = `send${toPascal(t.camelName)}`;
        if (t.schema && t.schema.length > 0) {
          const params = t.schema
            .map((f) => `${javaType(f.type)} ${toCamel(f.key)}`)
            .join(', ');
          const mapEntries = t.schema
            .map((f) => `        data.put("${f.key}", ${toCamel(f.key)});`)
            .join("\n");
          return `    public Map<String, Object> ${methodName}(String recipient, ${params}) throws Exception {\n        Map<String, Object> data = new HashMap<>();\n${mapEntries}\n        return send("${t.camelName}", recipient, data);\n    }`;
        }
        return `    public Map<String, Object> ${methodName}(String recipient, Map<String, Object> data) throws Exception {\n        return send("${t.camelName}", recipient, data);\n    }`;
      }),
    )
    .join("\n\n");

  return `// MailService.java — generated by Mail Service SDK Export
import java.net.http.*;
import java.net.URI;
import java.util.*;
import com.fasterxml.jackson.databind.ObjectMapper;

public class MailService {
    private static final ObjectMapper MAPPER = new ObjectMapper();
    private final String serviceUrl;

    public MailService() {
        this.serviceUrl = System.getenv("${config.serviceUrlVarName}") != null
            ? System.getenv("${config.serviceUrlVarName}").replaceAll("/$", "")
            : "";
    }

    private String getKey(String app) {
        String env = MailConfig.APPS.get(app).keyEnv();
        return Optional.ofNullable(System.getenv(env)).orElse("");
    }

    private Map<String, Object> post(String app, String path, Map<String, Object> body) throws Exception {
        if (serviceUrl.isEmpty() || getKey(app).isEmpty()) {
            return Map.of("success", false, "error", "not configured");
        }
        String json = MAPPER.writeValueAsString(body);
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(serviceUrl + path))
            .header("Content-Type", "application/json")
            .header("X-API-KEY", getKey(app))
            .POST(HttpRequest.BodyPublishers.ofString(json))
            .build();
        HttpResponse<String> response = HttpClient.newHttpClient()
            .send(request, HttpResponse.BodyHandlers.ofString());
        return MAPPER.readValue(response.body(), Map.class);
    }

    @SuppressWarnings("unchecked")
    public Map<String, Object> send(String template, String recipient, Map<String, Object> data) throws Exception {
        MailConfig.TemplateConfig t = MailConfig.TEMPLATES.get(template);
        return post(t.app(), "/v1/send", Map.of(
            "template_slug", t.slug(), "recipient", recipient, "data", data
        ));
    }

${typedMethods}
}
`;
}

export const javaGenerator: LanguageGenerator = {
  id: 'java',
  name: 'Java',
  category: 'full-sdk',
  color: '#ED8B00',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'MailConfig.java', content: genConfig(config), syntax: 'java' },
      { filename: 'MailService.java', content: genService(config), syntax: 'java' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
